import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useSettingsStore } from '../store/settings';
import { userApi } from '../api/user';
import { authApi } from '../api/auth';
import { SectionCard } from '../components/ui/SectionCard';
import { Toggle } from '../components/ui/Toggle';
import { GroupManager } from '../components/GroupManager';
import { ImportWizard } from '../components/ImportWizard';
import { THEMES } from '../lib/theme';
import { isElectron } from '../lib/electron';
import { getDataSource } from '../data';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import type { ThemeName } from '@daynote/shared-types';

export function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const { setting, updateSetting } = useSettingsStore();
  const [nickname, setNickname] = useState(user?.nickname ?? '');
  const mode = useAuthStore((s) => s.mode);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [startupEnabled, setStartupEnabled] = useState(false);

  useEffect(() => {
    setNickname(user?.nickname ?? '');
  }, [user?.nickname]);

  // 查询开机启动状态（仅桌面端）
  useEffect(() => {
    if (!isElectron) return;
    window.electronAPI?.getStartup().then(setStartupEnabled).catch(() => {});
  }, []);

  const handleStartupToggle = useCallback(async (v: boolean) => {
    setStartupEnabled(v);
    await window.electronAPI?.setStartup(v);
  }, []);

  const handleLanguageChange = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
  }, [i18n]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const updated = await userApi.updateMe({ nickname });
      setUser(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = async (theme: ThemeName) => {
    await updateSetting({ theme });
  };

  const handleBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { url } = await userApi.uploadBackground(file);
    await updateSetting({ backgroundUrl: url });
  };

  const handleDeleteAccount = async () => {
    if (!confirm(t('settings.privacy.deleteConfirm'))) return;
    await authApi.deleteAccount();
    logout();
    navigate('/login');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await getDataSource().exportData();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `daynote-export-${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('settings.dataMigration.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const data = await getDataSource().exportData();
      const { exportToExcel } = await import('../lib/excel');
      const blob = await exportToExcel(data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `daynote-export-${dateStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('settings.dataMigration.exportExcelFailed'));
    } finally {
      setExportingExcel(false);
    }
  };

  const avatarLetter = (user?.nickname || 'U').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen p-6 md:p-8 max-w-3xl mx-auto">
      <header className="flex justify-between items-center mb-10">
        <button
          type="button"
          onClick={() => navigate('/calendar')}
          className="text-sm text-daynote-muted hover:text-daynote-text bg-transparent border-none cursor-pointer"
        >
          {t('settings.backToCalendar')}
        </button>
        <h1 className="font-display text-3xl">{t('settings.title')}</h1>
        {isElectron ? <div className="w-16" /> : <LanguageSwitcher />}
      </header>

      {mode === 'local' && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-daynote-accent/10 border border-daynote-accent/30 text-sm text-daynote-accent flex items-start gap-2">
          <span className="mt-0.5 flex-shrink-0">💾</span>
          <span>
            {isElectron
              ? t('settings.localMode.electron')
              : <>
                  {t('settings.localMode.web')}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="underline cursor-pointer bg-transparent border-none text-daynote-accent"
                  >
                    {t('settings.localMode.webLogin')}
                  </button>{' '}
                  {t('settings.localMode.webSuffix')}
                </>
            }
          </span>
        </div>
      )}

      {mode === 'cloud' && (
      <SectionCard title={t('settings.sections.account')}>
        <div className="flex items-center gap-5 mb-6">
          <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-daynote-accent to-daynote-warm flex items-center justify-center font-display text-[28px] font-semibold text-[#2a2520] overflow-hidden">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              avatarLetter
            )}
          </div>
          <div>
            <div className="text-lg font-medium">{user?.nickname}</div>
            <div className="text-[13px] text-daynote-muted">{user?.email || user?.phone || t('settings.account.unbound')}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] tracking-widest uppercase text-daynote-text/50 block mb-1.5">
              {t('settings.account.nickname')}
            </label>
            <input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-md px-3 py-2.5 text-[13px] focus:outline-none focus:border-daynote-accent"
            />
          </div>
          <div>
            <label className="text-[11px] tracking-widest uppercase text-daynote-text/50 block mb-1.5">
              {t('settings.account.phone')}
            </label>
            <input
              value={user?.phone || t('settings.account.unbound')}
              disabled
              className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-md px-3 py-2.5 text-[13px] opacity-50"
            />
          </div>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={saveProfile}
          className="mt-4 px-4 py-2 text-sm bg-daynote-accent text-[#2a2520] rounded-md font-medium border-none cursor-pointer disabled:opacity-60"
        >
          {saving ? t('settings.account.saving') : t('settings.account.save')}
        </button>
      </SectionCard>
      )}

      <SectionCard title={t('settings.sections.appearance')}>
        <div className="space-y-1">
          <div className="flex items-center justify-between py-4 border-b border-[var(--grid-line)]">
            <div>
              <div className="text-sm">{t('settings.appearance.showLunar')}</div>
              <div className="text-xs text-daynote-text/50 mt-0.5">{t('settings.appearance.showLunarDesc')}</div>
            </div>
            <Toggle
              checked={setting.showLunar}
              onChange={(v) => updateSetting({ showLunar: v })}
            />
          </div>
          <div className="flex items-center justify-between py-4 border-b border-[var(--grid-line)]">
            <div>
              <div className="text-sm">{t('settings.appearance.showHoliday')}</div>
              <div className="text-xs text-daynote-text/50 mt-0.5">{t('settings.appearance.showHolidayDesc')}</div>
            </div>
            <Toggle
              checked={setting.showHoliday}
              onChange={(v) => updateSetting({ showHoliday: v })}
            />
          </div>
          <div className="flex items-center justify-between py-4 border-b border-[var(--grid-line)]">
            <div>
              <div className="text-sm">{t('settings.appearance.weekStart')}</div>
            </div>
            <select
              value={setting.weekStart}
              onChange={(e) => updateSetting({ weekStart: Number(e.target.value) as 0 | 1 })}
              className="bg-white/[0.04] border border-[var(--grid-line)] rounded-md px-3 py-1.5 text-sm focus:outline-none"
            >
              <option value={1}>{t('settings.appearance.weekStartMon')}</option>
              <option value={0}>{t('settings.appearance.weekStartSun')}</option>
            </select>
          </div>
          <div className="py-4 border-b border-[var(--grid-line)]">
            <div className="text-sm mb-3">{t('settings.appearance.theme')}</div>
            <div className="flex flex-wrap gap-2">
              {THEMES.map((th) => (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => handleThemeChange(th.id)}
                  className={`px-3 py-1.5 text-xs rounded-md border cursor-pointer ${
                    setting.theme === th.id
                      ? 'border-daynote-accent text-daynote-accent bg-daynote-accent/10'
                      : 'border-[var(--grid-line)] text-daynote-muted hover:text-daynote-text'
                  }`}
                >
                  {t(`settings.appearance.themeNames.${th.id}`, th.label)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleThemeChange('auto')}
                className={`px-3 py-1.5 text-xs rounded-md border cursor-pointer ${
                  setting.theme === 'auto'
                    ? 'border-daynote-accent text-daynote-accent bg-daynote-accent/10'
                    : 'border-[var(--grid-line)] text-daynote-muted'
                }`}
              >
                {t('settings.appearance.themeAuto')}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between py-4 border-b border-[var(--grid-line)]">
            <div>
              <div className="text-sm">{t('settings.appearance.background')}</div>
              <div className="text-xs text-daynote-text/50 mt-0.5">{t('settings.appearance.backgroundDesc')}</div>
            </div>
            <label className="px-3 py-1.5 text-xs border border-[var(--grid-line)] rounded-md cursor-pointer hover:border-daynote-accent">
              {t('settings.appearance.upload')}
              <input type="file" accept="image/*" className="sr-only" onChange={handleBackgroundUpload} />
            </label>
          </div>
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="text-sm">{t('settings.appearance.maxRows')}</div>
              <div className="text-xs text-daynote-text/50 mt-0.5">{t('settings.appearance.maxRowsDesc')}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => updateSetting({ maxRows: Math.max(1, (setting.maxRows ?? 5) - 1) })}
                className="w-7 h-7 flex items-center justify-center bg-white/[0.04] border border-[var(--grid-line)] rounded text-daynote-muted hover:text-daynote-text hover:border-daynote-accent bg-transparent cursor-pointer text-lg leading-none"
              >
                −
              </button>
              <span className="w-6 text-center text-sm font-display font-medium">
                {setting.maxRows ?? 5}
              </span>
              <button
                type="button"
                onClick={() => updateSetting({ maxRows: Math.min(10, (setting.maxRows ?? 5) + 1) })}
                className="w-7 h-7 flex items-center justify-center bg-white/[0.04] border border-[var(--grid-line)] rounded text-daynote-muted hover:text-daynote-text hover:border-daynote-accent bg-transparent cursor-pointer text-lg leading-none"
              >
                ＋
              </button>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('settings.sections.groups')}>
        <GroupManager />
      </SectionCard>

      <SectionCard title={t('settings.sections.reports')}>
        <p className="text-sm text-daynote-muted mb-4 leading-relaxed">
          {t('settings.reports.desc')}
        </p>
        <Link
          to="/reports"
          className="inline-block px-4 py-2 text-sm bg-daynote-accent/15 text-daynote-accent border border-daynote-accent/30 rounded-lg hover:bg-daynote-accent/25 transition-colors"
        >
          {t('settings.reports.view')}
        </Link>
      </SectionCard>

      <SectionCard title={t('settings.sections.dataMigration')}>
        <p className="text-sm text-daynote-muted mb-4 leading-relaxed">
          {mode === 'local'
            ? t('settings.dataMigration.descLocal')
            : t('settings.dataMigration.descCloud')}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 text-sm bg-daynote-accent/15 text-daynote-accent border border-daynote-accent/30 rounded-lg hover:bg-daynote-accent/25 transition-colors cursor-pointer disabled:opacity-60"
          >
            {exporting ? t('settings.dataMigration.exporting') : t('settings.dataMigration.exportJson')}
          </button>
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exportingExcel}
            className="px-4 py-2 text-sm bg-daynote-accent/15 text-daynote-accent border border-daynote-accent/30 rounded-lg hover:bg-daynote-accent/25 transition-colors cursor-pointer disabled:opacity-60"
          >
            {exportingExcel ? t('settings.dataMigration.exporting') : t('settings.dataMigration.exportExcel')}
          </button>
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="px-4 py-2 text-sm border border-[var(--grid-line)] text-daynote-muted rounded-lg hover:border-daynote-accent hover:text-daynote-text transition-colors bg-transparent cursor-pointer"
          >
            {t('settings.dataMigration.importing')}
          </button>
        </div>
      </SectionCard>

      {/* 桌面端专属：小组件设置 */}
      {isElectron && (
        <SectionCard title={t('settings.sections.widget')}>
          <div className="space-y-1">
            <div className="flex items-center justify-between py-4 border-b border-[var(--grid-line)]">
              <div>
                <div className="text-sm">{t('settings.widget.startup')}</div>
                <div className="text-xs text-daynote-text/50 mt-0.5">{t('settings.widget.startupDesc')}</div>
              </div>
              <Toggle checked={startupEnabled} onChange={handleStartupToggle} />
            </div>
            <div className="py-4">
              <div className="text-xs text-daynote-text/50 leading-relaxed">
                {t('settings.widget.hint')}
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* 语言切换 */}
      <SectionCard title={t('settings.sections.language')}>
        <div className="flex items-center justify-between py-2">
          <div className="text-sm">{t('settings.language.label')}</div>
          <div className="flex gap-1 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg p-1">
            {(['zh', 'en'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => handleLanguageChange(lang)}
                className={`px-3 py-1 text-xs rounded-md cursor-pointer border-none ${
                  i18n.language.startsWith(lang)
                    ? 'bg-daynote-accent text-[#2a2520] font-medium'
                    : 'bg-transparent text-daynote-muted hover:text-daynote-text'
                }`}
              >
                {t(`settings.language.${lang}`)}
              </button>
            ))}
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('settings.sections.privacy')}>
        <div>
          <Link to="/privacy" className="flex justify-between items-center py-3 text-sm border-b border-[var(--grid-line)] hover:text-daynote-accent transition-colors">
            {t('settings.privacy.policy')} <span>→</span>
          </Link>
          <Link to="/terms" className="flex justify-between items-center py-3 text-sm border-b border-[var(--grid-line)] hover:text-daynote-accent transition-colors">
            {t('settings.privacy.terms')} <span>→</span>
          </Link>
          <a
            href="mailto:feedback@daynote.app"
            className="flex justify-between items-center py-3 text-sm hover:text-daynote-accent transition-colors"
          >
            <span>{t('settings.privacy.feedback')}</span>
            <span className="text-daynote-muted text-xs">feedback@daynote.app →</span>
          </a>
        </div>
        {mode === 'cloud' && (
          <button
            type="button"
            onClick={handleDeleteAccount}
            className="mt-6 px-4 py-2 text-sm border border-daynote-work text-daynote-work rounded hover:bg-daynote-work hover:text-white transition-colors bg-transparent cursor-pointer"
          >
            {t('settings.privacy.deleteAccount')}
          </button>
        )}
      </SectionCard>

      <p className="text-xs text-daynote-muted text-center pb-8">{t('settings.version')}</p>

      <ImportWizard open={showImport} onClose={() => setShowImport(false)} />
    </div>
  );
}
